import {
  createFileRoute,
  Link,
  useSearch,
} from "@tanstack/react-router";
import { Schema } from "effect";
import { useEffect, useState } from "react";

import {
  signInWithEmail,
  signInWithGithub,
  signInWithGoogle,
} from "@/auth/auth-client";
import { OAuthAuthorizationSearchSchema } from "@/auth/oauth-types";
import { StructuredData } from "@/shared/components/structured-data";
import {
  seo,
  createPageTitle,
  getCanonicalLinks,
} from "@/utils/seo";
// oxlint-disable-next-line import/no-unassigned-import -- CSS side-effect import is the canonical pattern for scoped styles
import "@/speech-agent/speech-agent.css";

const SIGN_IN_TITLE_PARTS = ["Přihlášení"];

function SignInPage() {
  const search = useSearch({ from: "/sign-in" });
  const isOAuthFlow = Boolean(
    search.response_type ||
    search.client_id ||
    search.redirect_uri
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.body.classList.add("in-speech-agent");
    return () => {
      document.body.classList.remove("in-speech-agent");
    };
  }, []);

  const callbackURL = isOAuthFlow
    ? `/oauth/consent${typeof window !== "undefined" ? window.location.search : ""}`
    : "/app/speech-agent";

  const handleEmailSubmit = async (
    e: React.SyntheticEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await signInWithEmail(
        email,
        password,
        callbackURL
      );
      if (result.error) {
        setError(
          result.error.message ??
            "Neplatný e-mail nebo heslo."
        );
      }
    } catch {
      setError("Při přihlášení nastala chyba.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="canvas" aria-hidden="true" />
      <div className="grain" aria-hidden="true" />
      <div className="app-shell">
        <StructuredData
          url="https://speech-agent.cz/sign-in"
          description="Přihlas se do Speech Agent — konverzační hlasový agent pro zápis z porad."
          about="User Authentication"
          titleParts={SIGN_IN_TITLE_PARTS}
        />
        <header className="site-header">
          <Link to="/sign-in" className="brand">
            <span className="brand-mark" aria-hidden="true">
              <span className="brand-mark-dot" />
            </span>
            <span className="brand-name">
              Speech
              <span className="brand-name-thin">
                {" "}
                Agent
              </span>
            </span>
          </Link>
        </header>
        <main>
          <section className="page" id="page-login">
            <div className="auth-shell">
              <div className="auth-side">
                <p className="kicker">— 01 / Vítej zpět</p>
                <h1 className="display">
                  Tvoje porady,
                  <br />
                  <em>v jednom dechu.</em>
                </h1>
                <p className="lede">
                  Promluv si s agentem o tom, co se na
                  schůzce dělo. Z rozhovoru vznikne
                  strukturovaný zápis — připravený k
                  odeslání.
                </p>
                <ul className="feature-list">
                  <li>
                    <span className="num">01</span>
                    Plynulý hlasový rozhovor v češtině.
                  </li>
                  <li>
                    <span className="num">02</span>
                    Strukturovaný Markdown výstup.
                  </li>
                  <li>
                    <span className="num">03</span>
                    Historie, PDF, vlastní agenti.
                  </li>
                </ul>
              </div>

              <div className="card auth-card">
                <p className="kicker">— Přihlášení</p>
                <h2 className="card-title">Otevřít účet</h2>
                <form
                  onSubmit={handleEmailSubmit}
                  autoComplete="on"
                >
                  <label
                    className="field-label"
                    htmlFor="login-email"
                  >
                    E-mail
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="jmeno@firma.cz"
                    value={email}
                    onChange={(e) =>
                      setEmail(e.target.value)
                    }
                  />
                  <label
                    className="field-label"
                    htmlFor="login-password"
                  >
                    Heslo
                  </label>
                  <input
                    id="login-password"
                    type="password"
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) =>
                      setPassword(e.target.value)
                    }
                  />
                  <button
                    type="submit"
                    className="primary-btn"
                    disabled={submitting}
                  >
                    {submitting
                      ? "Přihlašuji…"
                      : "Přihlásit"}
                    <span className="arrow">→</span>
                  </button>
                  {error && (
                    <p className="status error">{error}</p>
                  )}
                </form>
                <p className="auth-switch">
                  Nemáš účet?{" "}
                  <Link to="/sign-up">Zaregistruj se</Link>
                </p>
                <details
                  style={{
                    marginTop: "1rem",
                    color: "var(--fg-muted)",
                    fontSize: "0.85rem",
                  }}
                >
                  <summary>Jiné způsoby přihlášení</summary>
                  <div
                    style={{
                      marginTop: "0.5rem",
                      display: "flex",
                      gap: "0.5rem",
                      flexDirection: "column",
                    }}
                  >
                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={() =>
                        void signInWithGithub(callbackURL)
                      }
                    >
                      Přihlásit přes Github
                    </button>
                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={() =>
                        void signInWithGoogle(callbackURL)
                      }
                    >
                      Přihlásit přes Google
                    </button>
                  </div>
                </details>
              </div>
            </div>
          </section>
        </main>
        <footer className="site-footer">
          <span className="footer-mark">⌖</span>
          <span className="footer-text">
            Speech Agent · PoC
          </span>
        </footer>
      </div>
    </>
  );
}

export const Route = createFileRoute("/sign-in")({
  component: SignInPage,
  validateSearch: Schema.standardSchemaV1(
    OAuthAuthorizationSearchSchema
  ),
  head: () => ({
    meta: [
      ...seo({
        title: createPageTitle("Přihlášení"),
        description:
          "Přihlas se do Speech Agent — konverzační hlasový agent pro zápis z porad.",
        keywords:
          "speech agent, přihlášení, hlasový agent, zápis z porady",
        canonical: "https://speech-agent.cz/sign-in",
      }),
    ],
    links: [
      ...getCanonicalLinks(
        "https://speech-agent.cz/sign-in"
      ),
    ],
  }),
});
